import { useEffect, useState } from "react";
import { ScanLine, FileText, ShieldCheck, Globe, Car, MapPin } from "lucide-react";

const STEPS = [
  { Icon: FileText, label: "Reading contract" },
  { Icon: ScanLine, label: "Checking disclosures" },
  { Icon: Globe, label: "Checking sources" },
  { Icon: Car, label: "Matching this VIN" },
  { Icon: MapPin, label: "Comparing prices" },
  { Icon: ShieldCheck, label: "Scoring compliance" },
];

export const AnalyzingState = () => {
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(4);
  const stepCount = STEPS.length;

  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % stepCount), 2200);
    return () => clearInterval(id);
  }, [stepCount]);

  // Linear progress toward the ~45s total target (AI read + capped price search). It fills steadily and
  // holds near the end; the bar never "completes" on its own — when the report is ready this whole screen
  // unmounts and the results appear (an effective snap to 100%).
  useEffect(() => {
    const TARGET_MS = 45000;
    const TICK = 150;
    const start = Date.now();
    const id = setInterval(() => {
      const pct = Math.min(97, 4 + (Date.now() - start) / TARGET_MS * 96);
      setProgress(pct);
    }, TICK);
    return () => clearInterval(id);
  }, []);

  const ActiveIcon = STEPS[active].Icon;

  return (
    <div data-testid="analyzing-state" className="relative overflow-hidden animate-fade-up rounded-2xl border border-border bg-card p-8 sm:p-14">
      <div className="pointer-events-none absolute inset-0 grain-texture opacity-60" aria-hidden="true" />
      <div className="relative flex flex-col items-center text-center">
        {/* scanning contract with a gold radar sweep */}
        <div
          data-testid="scan-visual"
          className="relative mb-8 h-44 w-64 overflow-hidden rounded-xl border border-white/10 bg-secondary/40 shadow-inner"
        >
          <div className="absolute inset-0 p-5" aria-hidden="true">
            <div className="h-3 w-24 rounded bg-white/20" />
            <div className="mt-3 h-2 w-full rounded bg-white/10" />
            <div className="mt-2 h-2 w-5/6 rounded bg-white/10" />
            <div className="mt-2 h-2 w-2/3 rounded bg-white/10" />
            <div className="mt-4 h-2 w-1/2 rounded bg-white/10" />
            <div className="mt-2 h-2 w-3/4 rounded bg-white/10" />
            <div className="mt-4 h-6 w-20 rounded bg-primary/25" />
          </div>
          {/* soft gold sweep */}
          <div
            className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 animate-radar bg-gradient-to-r from-transparent via-primary/45 to-transparent blur-[1px]"
            aria-hidden="true"
          />
          {/* bright scan line */}
          <div
            className="pointer-events-none absolute inset-y-0 left-0 w-0.5 animate-radar bg-primary shadow-[0_0_14px_3px_rgba(234,179,8,0.75)]"
            aria-hidden="true"
          />
        </div>
        <h2 className="font-heading text-2xl font-800 uppercase tracking-wide text-foreground sm:text-3xl">
          Analyzing your contract
        </h2>

        {/* live status line — rotates through the current stage so the wait feels active */}
        <div
          data-testid="analyzing-status"
          key={active}
          className="mt-5 flex animate-fade-up items-center gap-2 rounded-full border border-primary/30 bg-primary/[0.06] px-4 py-1.5"
        >
          <ActiveIcon className="h-4 w-4 text-primary" aria-hidden="true" />
          <span className="font-mono-plex text-xs font-600 uppercase tracking-[0.14em] text-primary">
            {STEPS[active].label}…
          </span>
        </div>

        {/* linear progress toward the processing time limit */}
        <div className="mt-8 w-full max-w-2xl">
          <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
            <div
              data-testid="analyzing-progress-bar"
              className="h-full rounded-full bg-primary shadow-[0_0_12px_1px_rgba(234,179,8,0.6)] transition-[width] duration-150 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mt-2 text-right font-mono-plex text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
            {Math.round(progress)}%
          </div>
        </div>

        <div className="mt-8 grid w-full max-w-2xl grid-cols-2 gap-3 sm:grid-cols-3">
          {STEPS.map(({ Icon, label }, i) => {
            const isActive = i === active;
            return (
              <div
                key={i}
                className={`flex flex-col items-center gap-2 rounded-xl border p-4 transition-all duration-500 ${
                  isActive
                    ? "-translate-y-1 border-primary/40 bg-primary/5 shadow-sm"
                    : "border-border bg-secondary/40"
                }`}
              >
                <Icon
                  className={`h-5 w-5 transition-colors duration-500 ${
                    isActive ? "text-primary" : "text-muted-foreground"
                  }`}
                  aria-hidden="true"
                />
                <span className="font-mono-plex text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
